import { useCallback, useEffect, useReducer, useRef } from "react";

import {
  MAX_RECORDING_DURATION_MS,
  RECORDING_TIMESLICE_MS,
  VIDEO_BITS_PER_SECOND,
} from "../constants";
import { initialState, recorderReducer } from "./recorderReducer";

const SUPPORTED_MIME_TYPE =
  [
    "video/webm;codecs=vp9,opus",
    "video/webm;codecs=vp8,opus",
    "video/webm",
    "video/mp4",
  ].find(type => MediaRecorder.isTypeSupported(type)) ?? "";

const revokeBlobUrl = (url: string | null) => {
  if (url) {
    URL.revokeObjectURL(url);
  }
};

const setupTrackListeners = (
  stream: MediaStream,
  mediaRecorderRef: React.MutableRefObject<MediaRecorder | null>,
  trackEndedHandlersRef: React.MutableRefObject<
    Map<MediaStreamTrack, () => void>
  >,
) => {
  stream.getTracks().forEach(track => {
    const handler = () => {
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
    };
    track.addEventListener("ended", handler);
    trackEndedHandlersRef.current.set(track, handler);
  });
};

interface UseVideoRecorderOptions {
  onRecordingError?: (error: Error) => void;
  onRecordingStopped?: () => void;
  stream: MediaStream | null;
}

interface UseVideoRecorderReturn {
  clearRecording: () => void;
  error: Error | null;
  isRecording: boolean;
  mimeType: string | null;
  recordedBlob: Blob | null;
  recordedUrl: string | null;
  startRecording: () => void;
  stopRecording: () => void;
}

export const useVideoRecorder = ({
  onRecordingError,
  onRecordingStopped,
  stream,
}: UseVideoRecorderOptions): UseVideoRecorderReturn => {
  const [state, dispatch] = useReducer(recorderReducer, initialState);

  const { error, mimeType, recordedBlob, recordedUrl, status } = state;

  // Always reflects the latest stream without needing it in startRecording's dep array
  const streamRef = useRef(stream);
  streamRef.current = stream;

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const maxDurationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const isRecordingRequestingRef = useRef(false);
  const trackEndedHandlersRef = useRef<Map<MediaStreamTrack, () => void>>(
    new Map(),
  );

  const clearMaxDurationTimer = useCallback(() => {
    if (maxDurationTimerRef.current) {
      clearTimeout(maxDurationTimerRef.current);
      maxDurationTimerRef.current = null;
    }
  }, []);

  const clearTrackListeners = useCallback(() => {
    trackEndedHandlersRef.current.forEach((handler, track) => {
      track.removeEventListener("ended", handler);
    });
    trackEndedHandlersRef.current.clear();
  }, []);

  const handleDataAvailable = useCallback((event: BlobEvent) => {
    if (event.data.size > 0) {
      chunksRef.current.push(event.data);
    }
  }, []);

  const handleRecorderStop = useCallback(() => {
    const blob = new Blob(chunksRef.current, {
      type: SUPPORTED_MIME_TYPE || "video/webm",
    });
    if (blob.size === 0) {
      const emptyRecordingError = new Error("Recording produced an empty file");
      dispatch({ type: "RECORDING_ERROR", error: emptyRecordingError });
      onRecordingError?.(emptyRecordingError);
      clearMaxDurationTimer();
      clearTrackListeners();
      return;
    }
    const url = URL.createObjectURL(blob);
    dispatch({ type: "RECORDING_STOPPED", blob, url });
    clearMaxDurationTimer();
    clearTrackListeners();
    onRecordingStopped?.();
  }, [
    clearMaxDurationTimer,
    clearTrackListeners,
    onRecordingError,
    onRecordingStopped,
  ]);

  const handleRecorderError = useCallback(() => {
    const recordingError = new Error("Recording failed");
    dispatch({ type: "RECORDING_ERROR", error: recordingError });
    clearMaxDurationTimer();
    clearTrackListeners();
    onRecordingError?.(recordingError);
  }, [clearMaxDurationTimer, clearTrackListeners, onRecordingError]);

  const startRecording = useCallback(() => {
    if (isRecordingRequestingRef.current) return;
    isRecordingRequestingRef.current = true;

    try {
      const currentStream = streamRef.current;

      if (!currentStream) {
        throw new Error("No stream available for recording");
      }

      chunksRef.current = [];

      const recorder = new MediaRecorder(currentStream, {
        mimeType: SUPPORTED_MIME_TYPE || undefined,
        videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
      });

      dispatch({
        type: "RECORDING_STARTED",
        mimeType: SUPPORTED_MIME_TYPE || null,
      });

      recorder.ondataavailable = handleDataAvailable;
      recorder.onstop = handleRecorderStop;
      recorder.onerror = handleRecorderError;

      mediaRecorderRef.current = recorder;
      recorder.start(RECORDING_TIMESLICE_MS);

      // Monitor tracks for unexpected endings (permissions revoked, device disconnected, etc.)
      setupTrackListeners(
        currentStream,
        mediaRecorderRef,
        trackEndedHandlersRef,
      );

      // Auto-stop at max duration
      maxDurationTimerRef.current = setTimeout(() => {
        if (mediaRecorderRef.current?.state === "recording") {
          mediaRecorderRef.current.stop();
        }
      }, MAX_RECORDING_DURATION_MS);
    } catch (err) {
      const recordingError = err as Error;
      dispatch({ type: "RECORDING_ERROR", error: recordingError });
      clearMaxDurationTimer();
      clearTrackListeners();
      onRecordingError?.(recordingError);
    } finally {
      isRecordingRequestingRef.current = false;
    }
  }, [
    clearMaxDurationTimer,
    clearTrackListeners,
    handleDataAvailable,
    handleRecorderError,
    handleRecorderStop,
    onRecordingError,
  ]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const clearRecording = useCallback(() => {
    revokeBlobUrl(recordedUrl);
    dispatch({ type: "RECORDING_CLEARED" });
    chunksRef.current = [];
  }, [recordedUrl]);

  // Cleanup on unmount
  useEffect(() => {
    const currentRecordedUrl = recordedUrl;
    return () => {
      clearMaxDurationTimer();
      clearTrackListeners();
      if (mediaRecorderRef.current?.state === "recording") {
        mediaRecorderRef.current.stop();
      }
      revokeBlobUrl(currentRecordedUrl);
    };
  }, [clearMaxDurationTimer, clearTrackListeners, recordedUrl]);

  return {
    clearRecording,
    error,
    isRecording: status === "recording",
    mimeType,
    recordedBlob,
    recordedUrl,
    startRecording,
    stopRecording,
  };
};
